/**
 * Кабінет «Пошта» (ML-W1; mail.md, розд. 6): `/mail?folder=<id>&thread=<id>` — лінивий чанк
 * (пошук ML-W6 — фільтри `f` і `period`; текст запиту — лише в пам'яті сторінки, SEC-110)
 * features/mail/, у першому екрані його немає. `/mail/link` — сторінка посилань листа: туди веде
 * кожне посилання рамки, рішення «прямий перехід чи попередження» приймає вона під час кліку.
 */
import { createRoute } from '@tanstack/react-router';
import { rootRoute } from './root.tsx';

export interface MailSearch {
  folder?: string;
  thread?: string;
  /** «Чернетки Clientline» (ML-W2) замість папки сервера. */
  view?: 'drafts';
  /** «Відповісти» з лінії картки (ML-W4): кабінет відкриває відповідь на тред і прибирає параметр. */
  compose?: 'reply';
  /** Пошук (ML-W6): швидкі фільтри через кому, період. Текст запиту в адресу не йде (SEC-110). */
  f?: string;
  period?: '7d' | '30d' | '365d';
}

export interface MailLinkSearch {
  fromMessage?: string;
  link?: number;
}

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const idOf = (value: unknown) => (typeof value === 'string' && ID.test(value) ? value : undefined);
const PERIODS = ['7d', '30d', '365d'] as const;
const FILTER = /^[a-z]{1,10}(,[a-z]{1,10}){0,9}$/;

export const mailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail',
  validateSearch: (search: Record<string, unknown>): MailSearch => {
    const folder = idOf(search.folder);
    const thread = idOf(search.thread);
    const period = PERIODS.find((value) => value === search.period);
    return {
      ...(folder ? { folder } : {}),
      ...(thread ? { thread } : {}),
      ...(search.view === 'drafts' ? { view: 'drafts' as const } : {}),
      ...(thread && search.compose === 'reply' ? { compose: 'reply' as const } : {}),
      ...(typeof search.f === 'string' && FILTER.test(search.f) ? { f: search.f } : {}),
      ...(period ? { period } : {}),
    };
  },
}).lazy(() => import('../../features/mail/MailPage.tsx').then((m) => m.MailRoute));

export const mailLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/link',
  validateSearch: (search: Record<string, unknown>): MailLinkSearch => {
    const fromMessage = idOf(search.fromMessage);
    const link = Number(search.link);
    return {
      ...(fromMessage ? { fromMessage } : {}),
      ...(Number.isInteger(link) && link >= 0 && link < 500 ? { link } : {}),
    };
  },
}).lazy(() => import('../../features/mail/MailLinkPage.tsx').then((m) => m.MailLinkRoute));
